import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { addEcwidItem, syncEcwidCart } from "../lib/ecwid";
import { klaviyoAddedToCart } from "../lib/klaviyo";

const CartContext = createContext(null);
const STORAGE_KEY = "fd_cart_v1";

let resyncTimer = null;

const toEcwidLines = (items) =>
  items.map((i) => ({ id: i.product_id, quantity: i.qty, options: i.ecwid_options || undefined }));

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const scheduleResync = (next) => {
    clearTimeout(resyncTimer);
    resyncTimer = setTimeout(() => {
      syncEcwidCart(toEcwidLines(next)).catch(() => {});
    }, 600);
  };

  const addItem = (product, qty = 1, option = null, ecwidOptions = null) => {
    const key = `${product.product_id}:${option?.label || ""}`;
    const unit = Math.round((product.price + (option?.markup || 0)) * 100) / 100;
    let nextItems = null;
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      nextItems = existing
        ? prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i))
        : [
            ...prev,
            {
              key,
              product_id: product.product_id,
              slug: product.slug,
              name: product.name,
              image: product.image,
              option: option?.label || null,
              ecwid_options: ecwidOptions || null,
              unit_price: unit,
              qty,
            },
          ];
      return nextItems;
    });
    addEcwidItem({ id: product.product_id, quantity: qty, options: ecwidOptions || undefined })
      .then((r) => {
        if (!r || r.success === false) return;
        const list = nextItems || [];
        const total = Math.round(list.reduce((a, i) => a + i.unit_price * i.qty, 0) * 100) / 100;
        klaviyoAddedToCart(product, qty, option?.label || null, unit, list, total);
      })
      .catch(() => {});
    setOpen(true);
  };

  const setQty = (key, qty) => {
    setItems((prev) => {
      const next = qty <= 0 ? prev.filter((i) => i.key !== key) : prev.map((i) => (i.key === key ? { ...i, qty } : i));
      scheduleResync(next);
      return next;
    });
  };

  const removeItem = (key) => {
    setItems((prev) => {
      const next = prev.filter((i) => i.key !== key);
      scheduleResync(next);
      return next;
    });
  };

  const clear = () => {
    setItems([]);
    scheduleResync([]);
  };

  const { count, subtotal } = useMemo(() => {
    return {
      count: items.reduce((a, i) => a + i.qty, 0),
      subtotal: Math.round(items.reduce((a, i) => a + i.unit_price * i.qty, 0) * 100) / 100,
    };
  }, [items]);

  return (
    <CartContext.Provider value={{ items, addItem, setQty, removeItem, clear, count, subtotal, open, setOpen }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
